import { FunctionComponent, useEffect } from 'react'
import { Box, Button, FormControl, FormHelperText, TextField } from '@mui/material'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'

import { IUser } from 'types/users'
import { zodResolver } from '@hookform/resolvers/zod'
import { createProfileFormSchema } from './validations/profile-form-validation'
import { useCreateExternalUserMutation } from 'services/users'

interface Props {
  currentUser?: IUser
}

export const ProfileForm: FunctionComponent<Props> = ({ currentUser }) => {
  const { t } = useTranslation()
  const { firstName, lastName, position, email, phoneNumber } = currentUser!
  const schema = createProfileFormSchema(t)
  const [createUser, { isLoading: isCreateUserLoading, isError, isSuccess }] =
    useCreateExternalUserMutation()

  const {
    handleSubmit,
    formState: { errors },
    register,
  } = useForm<IUser>({
    defaultValues: {
      firstName,
      lastName,
      position,
      email,
      phoneNumber,
    },
    resolver: zodResolver(schema),
  })

  const onSubmit = (values: IUser) => {
    createUser({ ...currentUser, ...values })
  }

  useEffect(() => {
    if (isSuccess) {
      toast.success(t('operation-success'))
    }
  }, [isSuccess, t])

  return (
    <form onSubmit={handleSubmit(onSubmit)} id="profile-form">
      <FormControl sx={{ minWidth: 120 }} fullWidth margin="dense">
        <TextField
          size="small"
          label={<>{t('firstName')}</>}
          variant="outlined"
          error={!!errors.firstName}
          {...register('firstName')}
        />

        {errors.firstName && (
          <FormHelperText error>{errors.firstName?.message}</FormHelperText>
        )}
      </FormControl>

      <FormControl sx={{ minWidth: 120 }} fullWidth margin="dense">
        <TextField
          size="small"
          label={<>{t('lastName')}</>}
          variant="outlined"
          error={!!errors.lastName}
          {...register('lastName')}
        />

        {errors.lastName && (
          <FormHelperText error>{errors.lastName?.message}</FormHelperText>
        )}
      </FormControl>

      <FormControl sx={{ minWidth: 120 }} fullWidth margin="dense">
        <TextField
          size="small"
          label={<>{t('position')}</>}
          variant="outlined"
          error={!!errors.position}
          {...register('position')}
        />

        {errors.position && (
          <FormHelperText error>{errors.position?.message}</FormHelperText>
        )}
      </FormControl>

      <FormControl sx={{ minWidth: 120 }} fullWidth margin="dense">
        <TextField
          size="small"
          label={<>{t('phone-number')}</>}
          variant="outlined"
          error={!!errors.phoneNumber}
          {...register('phoneNumber', { required: t('required') })}
        />

        {errors.phoneNumber && (
          <FormHelperText error>{errors.phoneNumber?.message}</FormHelperText>
        )}
      </FormControl>

      <FormControl sx={{ minWidth: 120 }} fullWidth margin="dense">
        <TextField
          size="small"
          label={<>{t('email-address')}</>}
          variant="outlined"
          error={!!errors.email}
          {...register('email', { required: t('required') })}
        />

        {errors.email && (
          <FormHelperText error>{errors.email?.message}</FormHelperText>
        )}
      </FormControl>

      <Box display="flex" justifyContent="flex-end" gap={2} mt={2}>
        <Button
          type="submit"
          variant="contained"
          style={{ color: '#fff', backgroundColor: 'rgb(3, 34, 76)' }}
          color="primary"
          form="profile-form"
        >
          {t('save')}
        </Button>
      </Box>
    </form>
  )
}
